/** Report contracts and display calculations; financial values come from the backend. */
export type Interval = 'weekly' | 'monthly' | 'quarterly' | 'yearly';
/** The interval the Overview opens with; the options themselves come from the reporting interval lookup. */
export const defaultInterval: Interval = 'quarterly';
export type Comparison = 'year' | 'previous';
export interface Period { start: string; end: string; label: string }
export interface Amounts {
  revenue: number; serviceCost: number; overhead: number; profit: number; interest: number; tax: number; netIncome: number;
  operating: number; investing: number; financing: number; net: number;
}
export interface Balances { cash: number; receivables: number; payables: number; debt: number }
export interface Snapshot {
  period: Period; comparePeriod: Period; current: Amounts; compare: Amounts; ytd: Amounts; balance: Balances; compareBalance: Balances;
}
const day = 86400000;
const date = (text: string) => new Date(text + 'T00:00:00Z');
const iso = (value: Date) => value.toISOString().slice(0, 10);
export function shift(start: string, interval: Interval, count: number): string {
  const d = date(start);
  if (interval === 'weekly') d.setUTCDate(d.getUTCDate() + 7 * count);
  else d.setUTCMonth(d.getUTCMonth() + { monthly: 1, quarterly: 3, yearly: 12 }[interval] * count);
  return iso(d);
}
export function period(start: string, interval: Interval): Period {
  const d = date(start);
  const label = interval === 'weekly' ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    : interval === 'monthly' ? d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
    : interval === 'quarterly' ? `Q${Math.floor(d.getUTCMonth() / 3) + 1} ${d.getUTCFullYear()}` : String(d.getUTCFullYear());
  return { start, end: iso(new Date(+date(shift(start, interval, 1)) - day)), label };
}
/** The period a saved report covers, as report lists show it. */
export const reportPeriodLabel = (item: { interval: Interval; periodStart: string }) =>
  (item.interval === 'weekly' ? 'Week of ' : '') + period(item.periodStart, item.interval).label;
export function money(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return value === 0 ? '$0' : `${value < 0 ? '−' : ''}$${Math.abs(value) >= 1000 ? (Math.abs(value) / 1000).toFixed(2) + 'm' : Math.abs(value).toFixed(1) + 'k'}`;
}
export const signedMoney = (value: number) => (value > 0 ? '+' : '') + money(value);
export const percentagePoints = (value: number) => !Number.isFinite(value) ? 'No comparable baseline' : `${value >= 0 ? '+' : ''}${value.toFixed(1)} pp`;
export const growth = (value: number, base: number) => !Number.isFinite(value) || !Number.isFinite(base) || base <= 0 ? 'No comparable baseline' : `${value >= base ? '+' : ''}${(100 * (value / base - 1)).toFixed(1)}%`;
